import currencySymbolMap from "currency-symbol-map";
import { moduleLabelMap, moduleLimitKeysMap } from "../constants/planConstants";

export const normalizeListResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.subscriptions)) return response.data.subscriptions;
  if (Array.isArray(response?.result?.subscriptions)) return response.result.subscriptions;
  if (Array.isArray(response?.result)) return response.result;
  return [];
};

export const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  return String(value?._id || value?.id || "").trim();
};

export const toTitle = (value = "") => {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const formatPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "0";
  if (Number.isInteger(amount)) return String(amount);
  return amount.toFixed(2);
};

export const getBillingText = (billingCycle = "", currency = "") => {
  const cycle = String(billingCycle || "").toLowerCase();
  if (cycle === "monthly") return "Per month";
  if (cycle === "quarterly") return "Per quarter";
  if (cycle === "yearly" || cycle === "annual" || cycle === "annually") {
    return "Per year";
  }
  if (cycle) return `Per ${cycle}`;
  return currency ? `Billed in ${currency}` : "Custom billing";
};

// A subscription counts as the current plan only when the subscription
// itself is active AND its populated planId is active. If more than one
// qualifies, prefer the most recently updated.
export const getCurrentPlanId = (subscriptions = []) => {
  const eligible = (subscriptions || []).filter(
    (subscription) =>
      subscription?.isActive === true && subscription?.planId?.isActive === true
  );

  if (!eligible.length) return "";

  const sorted = [...eligible].sort(
    (a, b) =>
      new Date(b?.updatedAt || b?.createdAt || 0) -
      new Date(a?.updatedAt || a?.createdAt || 0)
  );

  return normalizeId(sorted[0]?.planId);
};

export const buildPlanFeatures = (plan = {}) => {
  const moduleAccess = plan?.moduleAccess || {};
  const moduleLimits = plan?.moduleLimits || {};

  const features = [];

  Object.entries(moduleAccess).forEach(([moduleKey, enabled]) => {
    if (enabled !== true) return;

    const relatedLimitKeys = moduleLimitKeysMap[moduleKey] || [];
    let hasLimit = false;

    relatedLimitKeys.forEach((limitKey) => {
      const value = moduleLimits?.[limitKey];
      if (value === undefined || value === null || value === "") return;

      hasLimit = true;
      if (Array.isArray(value)) {
        features.push(`${limitKey}: ${value.join(", ")}`);
      } else {
        features.push(`${limitKey}: ${value}`);
      }
    });

    if (!hasLimit && moduleLabelMap[moduleKey]) {
      features.push(moduleLabelMap[moduleKey]);
    }
  });

  if (features.length) return features;

  return ["Core module access", "Standard limits", "Plan support included"];
};

// Resolve the country-specific price/currency for a plan card. The country
// endpoint puts the resolved entry on `priceForCountry`; fall back to the first
// active pricing[] entry, then the (legacy) base price. Render verbatim — never
// the convertedPrice/convertedCurrency.
export const getPlanCountryPrice = (plan = {}) => {
  const resolved =
    plan?.priceForCountry ||
    (Array.isArray(plan?.pricing)
      ? plan.pricing.find((p) => p?.isActive === true)
      : null) ||
    null;

  if (resolved) {
    return {
      price: resolved.price,
      currency: resolved.currency,
    };
  }
  return {
    price: plan?.price ?? plan?.basePrice,
    currency: plan?.currency || plan?.baseCurrency,
  };
};

export const mapApiPlansToCards = (plans = [], fallbackCurrentPlanId = "") => {
  const sortedPlans = [...plans].sort(
    (a, b) => Number(getPlanCountryPrice(a).price || 0) - Number(getPlanCountryPrice(b).price || 0)
  );

  if (!sortedPlans.length) return [];

  const resolvedFallbackCurrentPlanId = normalizeId(fallbackCurrentPlanId);
  const isPlanCurrent = (plan) => {
    if (plan?.currentPlan === true) return true;
    if (!resolvedFallbackCurrentPlanId) return false;
    return normalizeId(plan?._id || plan?.id) === resolvedFallbackCurrentPlanId;
  };

  const currentPlan = sortedPlans.find(isPlanCurrent);
  const currentPlanId = normalizeId(currentPlan?._id || currentPlan?.id);

  const highestPlanId = normalizeId(
    sortedPlans[sortedPlans.length - 1]?._id ||
      sortedPlans[sortedPlans.length - 1]?.id
  );

  const popularCandidate = sortedPlans.find((plan) => {
    const id = normalizeId(plan?._id || plan?.id);
    return id && id !== currentPlanId && id !== highestPlanId;
  });
  const popularPlanId = normalizeId(popularCandidate?._id || popularCandidate?.id);

  return sortedPlans.map((plan) => {
    const id = normalizeId(plan?._id || plan?.id);
    const { price: countryPrice, currency: countryCurrency } = getPlanCountryPrice(plan);
    const currencyCode = String(countryCurrency || "").toUpperCase();
    const symbol = currencyCode
      ? currencySymbolMap(currencyCode) || `${currencyCode} `
      : "$";

    const isCurrent = isPlanCurrent(plan);
    const isSalesOnly =
      plan?.requiresSalesContact === true ||
      plan?.contactSales === true ||
      plan?.isCustom === true ||
      plan?.isEnterprise === true;

    return {
      id,
      name: plan?.planName || "Plan",
      description: `${toTitle(plan?.billingCycle || "custom")} billing in ${
        currencyCode || "default"
      } with configurable module limits.`,
      priceSign: symbol,
      currency: currencyCode || "ZAR",
      price: formatPrice(countryPrice),
      billingText: getBillingText(plan?.billingCycle, currencyCode),
      buttonText: isCurrent
        ? "Current Plan"
        : isSalesOnly
        ? "Contact Sales"
        : "Switch to this Plan",
      buttonType: isCurrent ? "current" : isSalesOnly ? "filled" : "outline",
      canSwitch: !isCurrent && !isSalesOnly,
      currentPlan: isCurrent,
      trialText: isCurrent
        ? "Your active subscription"
        : `Billing cycle: ${toTitle(plan?.billingCycle || "custom")}`,
      featuresInfo: `Includes ${
        Object.values(plan?.moduleAccess || {}).filter(Boolean).length
      } enabled modules`,
      popular: id === popularPlanId,
      features: buildPlanFeatures(plan),
    };
  });
};
